/** @jsx jsx */
import { jsx } from "@emotion/core"
import * as React from "react"
import { visuallyHiddenCss } from "../../stylesheets/a11y"
import { AtomTone } from "../../theme/types"
import { Theme } from "../../theme"
import ToggleGutter, {
  ToggleGutterTagName,
  toggleGutterCheckedCss,
  toggleGutterFocusCss,
  toggleGutterDisabledCss,
} from "./ToggleGutter"
import {
  toggleWrapperCss,
  toggleLabelCss,
  toggleHintCss,
  toggleLabelContentCss,
} from "./Toggle.styles"

type BaseToggleProps = Omit<JSX.IntrinsicElements["input"], "ref" | "type"> & {
  tone?: AtomTone
}

const BaseToggle = React.forwardRef<HTMLInputElement, BaseToggleProps>(
  function BaseToggle({ tone = `SUCCESS`, className, style, ...rest }, ref) {
    return (
      <React.Fragment>
        <input
          ref={ref}
          type="checkbox"
          value="true"
          css={(theme: Theme) => [
            visuallyHiddenCss,
            {
              [`&:disabled + ${ToggleGutterTagName}`]: toggleGutterDisabledCss(
                theme
              ),
              [`&:checked + ${ToggleGutterTagName}`]: toggleGutterCheckedCss(
                tone,
                rest.disabled
              )(theme),
              [`&:focus + ${ToggleGutterTagName}`]: toggleGutterFocusCss(theme),
            },
          ]}
          {...rest}
        />
        <ToggleGutter className={className} style={style} />
      </React.Fragment>
    )
  }
)

export type ToggleCheckboxProps = Omit<
  BaseToggleProps,
  "label" | "ref" | "id"
> & {
  id: string
  label: React.ReactNode
  labelPosition?: "start" | "end"
  hint?: React.ReactNode
}

export default React.forwardRef<HTMLInputElement, ToggleCheckboxProps>(
  function ToggleCheckbox(
    {
      id,
      label,
      hint,
      labelPosition = "end",
      className,
      style,
      disabled,
      ...rest
    },
    ref
  ) {
    return (
      <div css={toggleWrapperCss} className={className} style={style}>
        <label
          htmlFor={id}
          css={theme => [
            toggleLabelCss(theme),
            disabled && {
              color: theme.colors.grey[50],
              cursor: `not-allowed`,
            },
          ]}
        >
          <BaseToggle
            ref={ref}
            id={id}
            disabled={disabled}
            {...rest}
            css={theme => [
              labelPosition === `end`
                ? {
                    marginRight: theme.space[4],
                  }
                : {
                    marginLeft: theme.space[4],
                    order: 1,
                  },
            ]}
            aria-describedby={!!hint ? `${id}__hint` : ``}
          />
          <div css={toggleLabelContentCss}>{label}</div>
        </label>
        {hint && (
          <small
            id={`${id}__hint`}
            css={theme => [
              toggleHintCss(theme),
              disabled && {
                color: theme.colors.grey[50],
              },
            ]}
          >
            {hint}
          </small>
        )}
      </div>
    )
  }
)
